import React, { memo, useMemo, useCallback } from 'react';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProgressIndicatorProps } from '../types/interfaces';

const ProgressIndicator: React.FC<ProgressIndicatorProps> = memo(({
  currentStep,
  totalSteps,
  completedSteps = [],
  stepTitles = [],
  variant = 'horizontal'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Group steps into logical sections
  // Note: Step 1 (RequestType) has been removed - now integrated into HomeScreen
  // useMemo to prevent sections array recreation on every render
  const sections = useMemo(() => [
    { 
      name: t('progress.sections.applicant.title'), 
      steps: [2, 3, 4], 
      firstStep: 2,  // Now starts at step 2 (ContractSelection)
      description: t('progress.sections.applicant.description')
    },
    { 
      name: t('progress.sections.travel.title'), 
      steps: [5, 6, 7, 8], 
      firstStep: 5,
      description: t('progress.sections.travel.description')
    },
    { 
      name: t('progress.sections.event.title'), 
      steps: [9, 10], 
      firstStep: 9,
      description: t('progress.sections.event.description')
    },
    { 
      name: t('progress.sections.documents.title'), 
      steps: [11, 12, 13], 
      firstStep: 11,
      description: t('progress.sections.documents.description')
    },
    { 
      name: t('progress.sections.submission.title'), 
      steps: [14, 15, 16, 17, 18], 
      firstStep: 14,
      description: t('progress.sections.submission.description')
    }
  ], [t]);

  const getCurrentSection = useCallback(() => {
    return sections.find(section => section.steps.includes(currentStep)) || sections[0];
  }, [sections, currentStep]);

  const currentSection = getCurrentSection();
  const sectionIndex = sections.indexOf(currentSection);

  // Calculate progress based on current step position (1-20) - memoized
  const progressPercentage = useMemo(() =>
    Math.round(((currentStep - 1) / (totalSteps - 1)) * 100),
    [currentStep, totalSteps]
  );

  const handleSectionClick = useCallback((section: typeof sections[0]) => {
    // Navigate to the first step of the clicked section
    navigate(`/claim/step/${section.firstStep}`);
  }, [navigate]);

  const getStepStatus = (step: number) => {
    if (step === currentStep) return 'current';
    if (completedSteps.includes(step)) return 'completed';
    if (step < currentStep) return 'completed'; // Mark previous steps as completed
    return 'upcoming';
  };

  const getStepTitle = (step: number) => {
    if (stepTitles[step - 1]) return stepTitles[step - 1];
    return `${t('navigation.step', { current: step, total: totalSteps })}`;
  };

  const renderHorizontalProgress = () => (
    <div className="w-full">
      {/* Main progress bar */}
      <div className="mb-4">
        <div className="progress-bar h-3">
          <div 
            className="progress-fill h-3"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('navigation.progress', { percent: progressPercentage })}
          />
        </div>
      </div>
      
      {/* Clickable section indicators */}
      <div className="flex justify-between bg-white rounded-lg p-2">
        {sections.map((section, index) => {
          const isCompleted = section.steps.every(step => step < currentStep);
          const isCurrent = section.steps.includes(currentStep);
          const isUpcoming = section.steps.every(step => step > currentStep);
          const canNavigate = isCompleted || isCurrent || section.steps.some(step => completedSteps.includes(step));
          
          return (
            <button
              key={section.name}
              onClick={() => canNavigate && handleSectionClick(section)}
              disabled={!canNavigate}
              className={clsx(
                'flex-1 px-2 py-3 text-center transition-all duration-200 rounded-lg',
                {
                  'cursor-pointer hover:bg-slate-50 hover:shadow-md': canNavigate,
                  'cursor-not-allowed opacity-50': !canNavigate,
                  'bg-slate-100': isCurrent
                }
              )}
              aria-label={`${section.name} - ${section.description}`}
            >
              <div className="flex flex-col items-center">
                <div
                  className={clsx(
                    'w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-200',
                    {
                      'bg-green-500 text-white shadow-md': isCompleted,
                      'bg-tenant-primary text-white shadow-lg animate-pulse': isCurrent,
                      'bg-brand-gray-100 text-brand-gray-600': isUpcoming,
                      'transform hover:scale-110': canNavigate
                    }
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <span className="text-lg font-bold">
                      {index + 1}
                    </span>
                  )}
                </div>
                <span className={clsx(
                  'text-sm font-medium',
                  {
                    'text-green-600': isCompleted,
                    'text-tenant-primary': isCurrent,
                    'text-brand-gray-600': isUpcoming
                  }
                )}>
                  {section.name}
                </span>
                <span className="text-xs text-brand-gray-600 mt-0.5">
                  {section.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderVerticalProgress = () => (
    <div className="space-y-4">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const status = getStepStatus(step);
        
        return (
          <div
            key={step}
            className={clsx(
              'flex items-center space-x-3 p-3 rounded-lg transition-colors duration-200',
              {
                'bg-green-50 border-l-4 border-green-500': status === 'completed',
                'bg-slate-100 border-l-4 border-tenant-500': status === 'current',
                'bg-slate-50 border-l-4 border-brand-gray-300': status === 'upcoming'
              }
            )}
          >
            <div
              className={clsx(
                'w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium',
                {
                  'step-completed': status === 'completed',
                  'step-current': status === 'current',
                  'step-upcoming': status === 'upcoming'
                }
              )}
              aria-label={getStepTitle(step)}
            >
              {status === 'completed' ? (
                <Check className="w-4 h-4" aria-hidden="true" />
              ) : (
                <span>{step}</span>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className={clsx(
                'text-sm font-medium',
                {
                  'text-green-700': status === 'completed',
                  'text-tenant-primary': status === 'current',
                  'text-brand-gray-600': status === 'upcoming'
                }
              )}>
                {getStepTitle(step)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="w-full" role="navigation" aria-label="Progress indicator">
      {variant === 'horizontal' ? renderHorizontalProgress() : renderVerticalProgress()}
    </div>
  );
});

ProgressIndicator.displayName = 'ProgressIndicator';

export default ProgressIndicator;